import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { isProd } from '../../../devprodConfig';
import L from 'leaflet';
import KohdeTabBar from './KohdeTabBar';
import TaulukkoFooter from './TaulukkoFooter';
import { useReportTemplateContext } from '../ReportTemplate/ReportTemplateContext';
import { useRakennusData } from '../../../context/RakennusDataContext';
import { defaultProjectName } from '../../../utils/projectNaming';
import { mittausCacheSet, mittausCacheSubscribe, subscribeNayteTunnus } from '../../../utils/mittausCache';
import { findLinkedProjectId } from '../../../utils/ptsUtils';
import { parseRows, calcStats, calcDist, initCombinedCarbRows, PEITEPAKSUUS_RANGES } from '../../../utils/measurementUtils';
import {
  getDynamicPTSProjects,
  getMittausProjektitByPTS,
  createMittausProjekti,
  getMittausProjekti,
  renameMittausProjekti,
  deleteMittausProjekti,
  createMittausKohde,
  updateMittausKohde,
  deleteMittausKohde,
  updateMittausProjektiMeta,
} from '../../../api/api';

const YEARS_AHEAD = [10, 20, 30, 40, 50];

function fmtNum(v, d = 1) {
  if (v === null || v === undefined) return '—';
  return typeof v === 'number' ? (Number.isInteger(v) ? String(v) : v.toFixed(d)) : String(v);
}

function fmtPct(v) {
  if (!v && v !== 0) return '—';
  return (v * 100).toFixed(1) + ' %';
}

const C   = { border: '1px solid #adb5bd', padding: '7px 7px', fontSize: '0.8rem', verticalAlign: 'middle' };
const CH  = { ...C, background: '#04aa00', fontWeight: 700, textAlign: 'center', fontSize: '0.75rem', color: '#fff' };
const CL  = { ...C, background: '#f8f9fa', fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const CR  = { ...C, textAlign: 'right', whiteSpace: 'nowrap' };
const CLbl = { ...C, background: '#04aa00', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', color: '#fff' };

const inputSt = {
  border: '1px solid #ced4da', borderRadius: 3, padding: '2px 6px',
  fontSize: '0.8rem', background: '#fff', width: '100%',
};

function ScatterChart({ coverArr, carbonStats }) {
  if (!coverArr.length) return null;
  const W = 560, H = 200;
  const ml = 42, mr = 115, mt = 10, mb = 34;
  const cW = W - ml - mr;
  const cH = H - mt - mb;
  const rawMax = Math.max(...coverArr, carbonStats.max ?? 0);
  const maxY = Math.ceil(rawMax / 10) * 10 + 10;
  const xScale = i => ml + (coverArr.length > 1 ? (i / (coverArr.length - 1)) * cW : cW / 2);
  const yScale = v => mt + cH - (v / maxY) * cH;
  const yTicks = Array.from({ length: Math.floor(maxY / 10) + 1 }, (_, i) => i * 10);
  const step = Math.max(1, Math.ceil(coverArr.length / 6));
  const xTicks = Array.from({ length: Math.floor((coverArr.length - 1) / step) + 1 }, (_, i) => i * step)
    .concat(coverArr.length - 1)
    .filter((v, i, a) => a.indexOf(v) === i && v < coverArr.length)
    .sort((a, b) => a - b);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {yTicks.map(v => <line key={v} x1={ml} y1={yScale(v)} x2={ml + cW} y2={yScale(v)} stroke="#e0e0e0" strokeWidth={0.5} />)}
      <line x1={ml} y1={mt} x2={ml} y2={mt + cH} stroke="#666" strokeWidth={1} />
      <line x1={ml} y1={mt + cH} x2={ml + cW} y2={mt + cH} stroke="#666" strokeWidth={1} />
      {yTicks.map(v => (
        <g key={v}>
          <line x1={ml - 3} y1={yScale(v)} x2={ml} y2={yScale(v)} stroke="#666" strokeWidth={1} />
          <text x={ml - 5} y={yScale(v)} textAnchor="end" dominantBaseline="middle" fontSize={8} fill="#555">{v}</text>
        </g>
      ))}
      <text transform={`translate(10,${mt + cH / 2}) rotate(-90)`} textAnchor="middle" fontSize={8} fill="#555">Etäisyys [mm]</text>
      {xTicks.map(i => (
        <g key={i}>
          <line x1={xScale(i)} y1={mt + cH} x2={xScale(i)} y2={mt + cH + 3} stroke="#666" strokeWidth={1} />
          <text x={xScale(i)} y={mt + cH + 12} textAnchor="middle" fontSize={8} fill="#555">{i + 1}</text>
        </g>
      ))}
      <text x={ml + cW / 2} y={H - 3} textAnchor="middle" fontSize={8} fill="#555">Mittausten lukumäärä [kpl]</text>
      {carbonStats.max !== null && <line x1={ml} y1={yScale(carbonStats.max)} x2={ml + cW} y2={yScale(carbonStats.max)} stroke="#e74c3c" strokeWidth={1.5} />}
      {carbonStats.ka  !== null && <line x1={ml} y1={yScale(carbonStats.ka)}  x2={ml + cW} y2={yScale(carbonStats.ka)}  stroke="#2980b9" strokeWidth={1.5} strokeDasharray="6 3" />}
      {carbonStats.min !== null && <line x1={ml} y1={yScale(carbonStats.min)} x2={ml + cW} y2={yScale(carbonStats.min)} stroke="#27ae60" strokeWidth={1.5} strokeDasharray="2 3" />}
      {coverArr.map((v, i) => <circle key={i} cx={xScale(i)} cy={yScale(v)} r={2.5} fill="#888" fillOpacity={0.85} />)}
      <g transform={`translate(${ml + cW + 10},${mt + 4})`}>
        <circle cx={5} cy={6} r={3} fill="#888" />
        <text x={12} y={10} fontSize={8} fill="#333">Peitepaksuus</text>
        {carbonStats.max !== null && <><line x1={0} y1={22} x2={10} y2={22} stroke="#e74c3c" strokeWidth={1.5} /><text x={12} y={26} fontSize={8} fill="#333">Karb. max</text></>}
        {carbonStats.ka  !== null && <><line x1={0} y1={38} x2={10} y2={38} stroke="#2980b9" strokeWidth={1.5} strokeDasharray="4 2" /><text x={12} y={42} fontSize={8} fill="#333">Karb. ka</text></>}
        {carbonStats.min !== null && <><line x1={0} y1={54} x2={10} y2={54} stroke="#27ae60" strokeWidth={1.5} strokeDasharray="2 2" /><text x={12} y={58} fontSize={8} fill="#333">Karb. min</text></>}
      </g>
    </svg>
  );
}

function MeasurementTable({ label, rows, setRows, onCommit, rowLabels }) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const isMobile = useIsMobile();
  const inputRefs = useRef({});
  const addRow = () => setRows(prev => [...prev, '']);
  const confirmRemove = () => {
    if (pendingDelete === null) return;
    setRows(prev => {
      const next = prev.filter((_, idx) => idx !== pendingDelete);
      const result = next.length ? next : [''];
      onCommit(result);
      return result;
    });
    setPendingDelete(null);
  };
  const updateRow = (i, val) => setRows(prev => {
    const next = [...prev];
    next[i] = val;
    const isLast = i === next.length - 1;
    const isValid = !isNaN(parseFloat(String(val).replace(',', '.'))) && val !== '';
    if (isLast && isValid) next.push('');
    return next;
  });
  const handleKeyDown = (e, i) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setTimeout(() => {
        const next = inputRefs.current[i + 1];
        if (next) { next.focus(); next.select(); }
      }, 0);
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      setTimeout(() => {
        const indices = Object.keys(inputRefs.current).map(Number).filter(k => inputRefs.current[k]);
        if (!indices.length) return;
        const topIdx = Math.max(...indices);
        const target = inputRefs.current[topIdx];
        if (target) { target.focus(); target.select(); }
      }, 0);
    }
  };
  const handlePaste = (e, rowIndex) => {
    const text = e.clipboardData.getData('text');
    const parts = text.trim().split(/[\s,;\n\t]+/).filter(s => s);
    if (parts.length > 1) {
      e.preventDefault();
      setRows(prev => {
        const next = [...prev];
        parts.forEach((val, i) => {
          if (rowIndex + i < next.length) next[rowIndex + i] = val;
          else next.push(val);
        });
        onCommit(next);
        return next;
      });
    }
  };
  const validCount = parseRows(rows).length;
  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      <style>{`.pp-mm-input:focus { outline: none; border-color: #0d6efd !important; box-shadow: 0 0 0 2px rgba(13,110,253,.2); }`}</style>
      <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, marginBottom: 4, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {label} — {validCount} kpl
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <colgroup>
          <col style={{ width: 28 }} />
          <col style={isMobile ? {} : { width: 56 }} />
          {!isMobile && PEITEPAKSUUS_RANGES.map((_, i) => <col key={i} style={{ width: 40 }} />)}
          <col style={{ width: 22 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...CH, padding: '3px 4px' }}>#</th>
            <th style={CH}>mm</th>
            {!isMobile && PEITEPAKSUUS_RANGES.map(r => <th key={r.label} style={{ ...CH, fontSize: '0.62rem', padding: '3px 2px' }}>{r.label}</th>)}
            <th style={{ ...CH, padding: 2 }} />
          </tr>
        </thead>
        <tbody>
          {rows.every(r => String(r).trim() !== '') && (
            <tr>
              <td colSpan={isMobile ? 3 : 13} style={{ ...C, background: '#f8f9fa', padding: '3px 8px' }}>
                <button type="button" onClick={addRow} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#0d6efd', fontSize: '0.75rem', fontWeight: 600 }}>+ Lisää rivi</button>
              </td>
            </tr>
          )}
          {[...rows].reverse().map((row, displayIdx) => {
            const i = rows.length - 1 - displayIdx;
            const num = parseFloat(String(row).replace(',', '.'));
            const valid = !isNaN(num) && num > 0;
            const rangeIdx = valid ? PEITEPAKSUUS_RANGES.findIndex(r => r.test(num)) : -1;
            return (
              <tr key={i}>
                <td style={{ ...C, textAlign: 'center', color: '#adb5bd', fontSize: '0.68rem', padding: '2px 3px' }}>{valid ? (rowLabels?.[i] ?? displayIdx) : ''}</td>
                <td style={{ ...C, padding: '2px 4px' }}>
                  <input ref={el => { inputRefs.current[i] = el; }} type="number" min={0} value={row}
                    onChange={e => updateRow(i, e.target.value)}
                    onBlur={() => onCommit(rows)}
                    onPaste={e => handlePaste(e, i)}
                    onKeyDown={e => handleKeyDown(e, i)}
                    className="pp-mm-input"
                    style={{ border: '1px solid #ced4da', borderRadius: 2, padding: isMobile ? '3px 5px' : '3px 6px', fontSize: '0.88rem', width: isMobile ? '100%' : 62, textAlign: 'right', caretColor: '#212529' }}
                    placeholder="mm" />
                </td>
                {!isMobile && PEITEPAKSUUS_RANGES.map((_, ri) => (
                  <td key={ri} style={{ ...C, textAlign: 'center', padding: '2px 3px', fontSize: '0.75rem', background: ri === rangeIdx ? '#d4edda' : undefined, fontWeight: ri === rangeIdx ? 700 : undefined, color: ri === rangeIdx ? '#155724' : '#dee2e6' }}>
                    {ri === rangeIdx ? '1' : ''}
                  </td>
                ))}
                <td style={{ ...C, textAlign: 'center', padding: 2 }}>
                  <button type="button" onClick={() => setPendingDelete(i)} title="Poista"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#adb5bd', fontSize: isMobile ? '1.1rem' : '0.9rem', lineHeight: 1, padding: isMobile ? '2px 4px' : 0 }}>×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Modal show={pendingDelete !== null} onHide={() => setPendingDelete(null)} centered size="sm">
        <Modal.Header closeButton><Modal.Title style={{ fontSize: '1rem' }}>Poista rivi</Modal.Title></Modal.Header>
        <Modal.Body style={{ fontSize: '0.9rem' }}>
          Haluatko varmasti poistaa rivin {pendingDelete !== null ? pendingDelete + 1 : ''}{pendingDelete !== null && rows[pendingDelete] ? ` (${rows[pendingDelete]} mm)` : ''}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setPendingDelete(null)}>Peruuta</Button>
          <Button variant="danger" size="sm" onClick={confirmRemove}>Poista</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

function BuildingMapModal({ rakennus, show, onHide }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  useEffect(() => {
    if (!show || !rakennus?.sijainti?.coordinates) return;
    const timer = setTimeout(() => {
      if (!mapRef.current) return;
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
      const [lng, lat] = rakennus.sijainti.coordinates;
      const map = L.map(mapRef.current, { zoomControl: true });
      mapInstanceRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors' }).addTo(map);
      map.setView([lat, lng], 16);
      const svgIcon = L.divIcon({ html: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 24 24" style="pointer-events:none;"><path fill="#007acc" stroke="white" stroke-width="0.75" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>`, className: '', iconSize: [40, 50], iconAnchor: [20, 50], popupAnchor: [0, -50] });
      L.marker([lat, lng], { icon: svgIcon }).addTo(map).bindPopup(rakennus.rakennustunnus || 'Rakennus').openPopup();
    }, 150);
    return () => { clearTimeout(timer); if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [show, rakennus]);
  const hasCoords = !!rakennus?.sijainti?.coordinates;
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>
          {rakennus?.rakennustunnus && <span style={{ fontFamily: 'monospace', marginRight: 8 }}>{rakennus.rakennustunnus}</span>}
          {[rakennus?.osoite, rakennus?.toimipaikka].filter(Boolean).join(', ')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        <div style={{ display: 'flex', gap: 20, padding: '12px 16px', background: '#f8f9fa', fontSize: '0.83rem', flexWrap: 'wrap' }}>
          <span><span style={{ color: '#6c757d' }}>Rakennustunnus: </span><strong>{rakennus?.rakennustunnus || '—'}</strong></span>
          <span><span style={{ color: '#6c757d' }}>Rakennusvuosi: </span><strong>{rakennus?.rakennusvuosi || '—'}</strong></span>
          {rakennus?.rakennusluokitus && <span><span style={{ color: '#6c757d' }}>Luokitus: </span>{rakennus.rakennusluokitus}</span>}
        </div>
        {hasCoords ? <div ref={mapRef} style={{ height: 340 }} /> : <div style={{ padding: 32, textAlign: 'center', color: '#6c757d' }}>Sijaintitietoja ei saatavilla</div>}
      </Modal.Body>
    </Modal>
  );
}

export default function PeitepaksuusBlock({ data, onChange, card, tableStartNumber, subsectionTitle, onCreateNayte, ptsProjectName }) {
  const rakennukset = card?.rakennukset_fulls ?? [];

  let ctxKiinteisto = null;
  let ctxSections = null;
  try {
    const ctx = useReportTemplateContext();
    ctxKiinteisto = ctx?.report?.id_kiinteisto ?? ctx?.rakennusData?.id_kiinteisto ?? null;
    ctxSections = ctx?.sections ?? null;
  } catch {}
  const { rakennusData: rdCtx } = useRakennusData?.() ?? {};
  const id_kiinteisto = ctxKiinteisto ?? rdCtx?.id_kiinteisto ?? card?.id_kiinteisto ?? null;
  const linkedPtsId = findLinkedProjectId(ctxSections);

  const [projekt, setProjekt]             = useState(null);
  const [activeKohdeId, setActiveKohdeId] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [coverRowsMap, setCoverRowsMap]   = useState({});
  const [carbonRowsMap, setCarbonRowsMap] = useState({});
  const [showInputs, setShowInputs]       = useState(true);
  const [ageInput, setAgeInput]           = useState('');
  const [allProjektit, setAllProjektit]   = useState([]);
  const [activeProjektiId, setActiveProjektiId] = useState(data?.mittausProjektiId ?? null);
  const isMobile = useIsMobile();

  // Project selector
  const [showSelector, setShowSelector]           = useState(false);
  const [ptsProjects, setPtsProjects]             = useState([]);
  const [selectedPtsId, setSelectedPtsId]         = useState(null);
  const [existingProjektit, setExistingProjektit] = useState([]);
  const [selectorTab, setSelectorTab]             = useState('new');
  const [newProjName, setNewProjName]             = useState('');
  const [newProjDate, setNewProjDate]             = useState('');
  const [newProjScope, setNewProjScope]           = useState('property');
  const [newProjRakennusId, setNewProjRakennusId] = useState(null);
  const [projNameError, setProjNameError]         = useState('');
  const [apiError, setApiError]                   = useState('');
  const [nayteCreateError, setNayteCreateError]   = useState(null);

  // Kohde management (tabs/rename/delete handled by KohdeTabBar)
  const [mapModalRakennus, setMapModalRakennus]         = useState(null);
  const [renamingProject, setRenamingProject]           = useState(false);
  const [renameProjectValue, setRenameProjectValue]     = useState('');
  const [pendingDeleteProject, setPendingDeleteProject] = useState(false);

  const saveTimers = useRef({});

  const projektRef = useRef(projekt);
  projektRef.current = projekt;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;

  const scheduleSave = useCallback((kohdeId, d) => {
    clearTimeout(saveTimers.current[kohdeId]);
    saveTimers.current[kohdeId] = setTimeout(() => {
      updateMittausKohde(kohdeId, { data: d }).catch(console.error);
    }, 1200);
  }, []);

  // Sync active project when navigating between sections
  useEffect(() => {
    setActiveProjektiId(data?.mittausProjektiId ?? null);
  }, [data?.mittausProjektiId]);

  // Load all projects for the PTS
  useEffect(() => {
    const ptsId = data?.ptsProjectId;
    if (!ptsId) { setAllProjektit([]); return; }
    getMittausProjektitByPTS(ptsId, 'peitepaksuus')
      .then(res => {
        const list = res.data ?? [];
        setAllProjektit(list);
        if (list.length > 0 && !data?.mittausProjektiId) {
          setActiveProjektiId(list[0].id);
          onChange?.({ ...data, mittausProjektiId: list[0].id });
        }
      })
      .catch(() => setAllProjektit([]));
  }, [data?.ptsProjectId]);

  // Re-sync local state when syncPoranayteRow adds a kohde via cache
  useEffect(() => {
    if (!activeProjektiId) return;
    return mittausCacheSubscribe(activeProjektiId, (updatedProj) => {
      setProjekt(updatedProj);
      setActiveKohdeId(prev => prev ?? updatedProj.kohteet?.[0]?.id ?? null);
      setCoverRowsMap(prev => {
        const next = { ...prev };
        (updatedProj.kohteet ?? []).forEach(k => {
          if (!next[k.id]) next[k.id] = (k.data?.cover_measurements ?? []).map(String).concat(['']);
        });
        return next;
      });
      setCarbonRowsMap(prev => {
        const next = { ...prev };
        (updatedProj.kohteet ?? []).forEach(k => {
          // Always refresh when karb_per_nayte is present (PoranayteBlock wrote new values).
          // Otherwise only init for new kohteet to avoid clobbering mid-edit state.
          if (k.data?.karb_per_nayte || !next[k.id]) {
            next[k.id] = initCombinedCarbRows(k.data?.carbonation_measurements);
          }
        });
        return next;
      });
    });
  }, [activeProjektiId]);

  useEffect(() => {
    if (!id_kiinteisto) return;
    getDynamicPTSProjects(id_kiinteisto).then(res => setPtsProjects(res.data ?? [])).catch(() => {});
  }, [id_kiinteisto]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!linkedPtsId || linkedPtsId === data?.ptsProjectId) return;
    onChange?.({ ...data, ptsProjectId: linkedPtsId, mittausProjektiId: null });
  }, [linkedPtsId, data?.ptsProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return subscribeNayteTunnus((nayteId, newTunnus) => {
      setProjekt(prev => {
        if (!prev) return prev;
        let changed = false;
        const kohteet = prev.kohteet.map(k => {
          if (k.nayte_id !== nayteId) return k;
          changed = true;
          const newData = { ...(k.data ?? {}), nayteTunnus: newTunnus };
          updateMittausKohde(k.id, { data: newData }).catch(console.error);
          return { ...k, data: newData };
        });
        return changed ? { ...prev, kohteet } : prev;
      });
    });
  }, []);

  // Load project from API
  useEffect(() => {
    if (!activeProjektiId) {
      setProjekt(null);
      setActiveKohdeId(null);
      setCoverRowsMap({});
      setCarbonRowsMap({});
      return;
    }
    setProjekt(null);
    setActiveKohdeId(null);
    setLoading(true);
    getMittausProjekti(activeProjektiId)
      .then(res => {
        const proj = res.data;
        setProjekt(proj);
        mittausCacheSet(proj.id, proj);
        const kohteet = proj.kohteet ?? [];
        const savedId = dataRef.current?.lastActiveKohdeId;
        const restoredId = kohteet.find(k => k.id === savedId)?.id ?? kohteet[0]?.id ?? null;
        setActiveKohdeId(restoredId);
        const cm = {}, cbm = {};
        kohteet.forEach(k => {
          cm[k.id] = (k.data?.cover_measurements ?? []).map(String).concat(['']);
          if (cm[k.id].length === 1) cm[k.id] = [''];
          cbm[k.id] = initCombinedCarbRows(k.data?.carbonation_measurements);
        });
        setCoverRowsMap(cm);
        setCarbonRowsMap(cbm);
        setAgeInput(String(proj.manual_age ?? ''));
        // Backfill ptsProjectId if missing (blocks saved before this feature)
        const ptsId = data?.ptsProjectId ?? proj.id_pts_project;
        if (ptsId) {
          if (!data?.ptsProjectId) onChange?.({ ...data, ptsProjectId: ptsId });
          getMittausProjektitByPTS(ptsId, 'peitepaksuus')
            .then(r => setAllProjektit(r.data ?? []))
            .catch(() => {});
        }
      })
      .catch(() => setProjekt(null))
      .finally(() => setLoading(false));
  }, [activeProjektiId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync ageInput when switching kohteet or when manual_age changes externally
  useEffect(() => {
    if (projekt) setAgeInput(String(projekt.manual_age ?? ''));
  }, [activeKohdeId, projekt?.manual_age]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the last-open kohde tab so the browser restores it on next visit
  useEffect(() => {
    if (!activeKohdeId) return;
    if (dataRef.current?.lastActiveKohdeId === activeKohdeId) return;
    onChangeRef.current?.({ ...dataRef.current, lastActiveKohdeId: activeKohdeId });
  }, [activeKohdeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSelector = async () => {
    if (!id_kiinteisto) return;
    const today = new Date().toISOString().split('T')[0];
    const rid = rakennukset[0]?.id_rakennus ?? null;
    setNewProjDate(today);
    setNewProjScope('property');
    setNewProjRakennusId(rid);
    setNewProjName(defaultProjectName('Peitepaksuus', subsectionTitle));
    setProjNameError('');
    setApiError('');
    setSelectorTab('new');
    try {
      const res = await getDynamicPTSProjects(id_kiinteisto);
      const pts = res.data ?? [];
      setPtsProjects(pts);
      setSelectedPtsId(pts[0]?.id ?? null);
      setExistingProjektit([]);
      if (pts[0]?.id) {
        const r2 = await getMittausProjektitByPTS(pts[0].id, 'peitepaksuus');
        setExistingProjektit(r2.data ?? []);
      }
      setShowSelector(true);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Lataus epäonnistui');
    }
  };

  const onPtsChange = async (ptsId) => {
    setSelectedPtsId(ptsId);
    try {
      const res = await getMittausProjektitByPTS(ptsId, 'peitepaksuus');
      setExistingProjektit(res.data ?? []);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Lataus epäonnistui');
    }
  };

  const suggestedName = (scope, rakennusId, date) => {
    if (scope === 'building') {
      const r = rakennukset.find(r => r.id_rakennus === rakennusId) ?? rakennukset[0];
      const tunnus = r?.rakennustunnus ?? '';
      return tunnus ? `Rakennus ${tunnus} ${date}` : `Rakennus ${date}`;
    }
    const addr = String(rakennukset[0]?.osoite ?? card?.osoite ?? '').split('\n')[0].trim();
    return addr ? `${addr} ${date}` : defaultProjectName('Peitepaksuus', subsectionTitle);
  };

  const refreshAllProjektit = (ptsId) =>
    getMittausProjektitByPTS(ptsId, 'peitepaksuus')
      .then(res => setAllProjektit(res.data ?? []))
      .catch(() => {});

  const createProject = async () => {
    if (!selectedPtsId) return;
    const name = newProjName.trim();
    if (!name) { setProjNameError('Nimi vaaditaan.'); return; }
    setProjNameError('');
    setApiError('');
    try {
      const res = await createMittausProjekti(selectedPtsId, 'peitepaksuus', name, {
        paivamaara: newProjDate || null,
        scope: newProjScope,
        id_rakennus: newProjScope === 'building' ? (newProjRakennusId ?? null) : null,
      });
      const proj = res.data;
      setProjekt(proj);
      setCoverRowsMap({});
      setCarbonRowsMap({});
      setActiveKohdeId((proj.kohteet ?? [])[0]?.id ?? null);
      setActiveProjektiId(proj.id);
      onChange?.({ ...data, mittausProjektiId: proj.id, ptsProjectId: selectedPtsId });
      refreshAllProjektit(selectedPtsId);
      setShowSelector(false);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Tarkastuskohteen luonti epäonnistui');
    }
  };

  const linkProject = async (id) => {
    setApiError('');
    try {
      const res = await getMittausProjekti(id);
      const proj = res.data;
      setProjekt(proj);
      mittausCacheSet(proj.id, proj);
      const kohteet = proj.kohteet ?? [];
      const cm = {}, cbm = {};
      kohteet.forEach(k => {
        cm[k.id]  = (k.data?.cover_measurements ?? []).map(String).concat(['']);
        cbm[k.id] = initCombinedCarbRows(k.data?.carbonation_measurements);
      });
      setCoverRowsMap(cm);
      setCarbonRowsMap(cbm);
      setActiveKohdeId(kohteet[0]?.id ?? null);
      setAgeInput(String(proj.manual_age ?? ''));
      setActiveProjektiId(id);
      onChange?.({ ...data, mittausProjektiId: id, ptsProjectId: selectedPtsId });
      refreshAllProjektit(selectedPtsId);
      setShowSelector(false);
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Tarkastuskohteen lataus epäonnistui');
    }
  };

  const commitRenameProject = async () => {
    const trimmed = renameProjectValue.trim();
    if (!trimmed || !projekt) { setRenamingProject(false); return; }
    try {
      await renameMittausProjekti(projekt.id, trimmed);
      setProjekt(p => ({ ...p, nimi: trimmed }));
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Uudelleennimeäminen epäonnistui');
    }
    setRenamingProject(false);
  };

  const confirmDeleteProject = async () => {
    if (!projekt) return;
    try {
      await deleteMittausProjekti(projekt.id);
      const remaining = allProjektit.filter(p => p.id !== projekt.id);
      setAllProjektit(remaining);
      const nextId = remaining[0]?.id ?? null;
      setActiveProjektiId(nextId);
      onChange?.({
        ...data,
        mittausProjektiId: projekt.id === data?.mittausProjektiId ? (nextId ?? null) : data?.mittausProjektiId,
      });
    } catch (err) {
      setApiError(err?.response?.data?.error ?? err.message ?? 'Poisto epäonnistui');
    }
    setPendingDeleteProject(false);
  };

  const handleKohdeCreate = async (name, withNayte = false, tunnus = '') => {
    let nayteId = null;
    if (withNayte && onCreateNayte) {
      try { const n = await onCreateNayte(name, tunnus); nayteId = n?.id ?? null; }
      catch (err) { if (err?.code === 'DUPLICATE_TUNNUS') { setNayteCreateError(err.message); return null; } }
    }
    const res = await createMittausKohde(projekt.id, name, (projekt.kohteet?.length ?? 0), nayteId);
    let kohdeData = null;
    if (tunnus) {
      kohdeData = { cover_measurements: [], carbonation_measurements: { alapinta: [], ylapinta: [] }, nayteTunnus: tunnus };
      await updateMittausKohde(res.data.id, { data: kohdeData }).catch(() => {});
    }
    const kohde = { ...res.data, nayte_id: nayteId, ...(kohdeData ? { data: kohdeData } : {}) };
    const updatedProjekt = { ...projekt, kohteet: [...(projekt.kohteet ?? []), kohde] };
    setProjekt(updatedProjekt);
    mittausCacheSet(updatedProjekt.id, updatedProjekt);
    setCoverRowsMap(prev => ({ ...prev, [kohde.id]: [''] }));
    setCarbonRowsMap(prev => ({ ...prev, [kohde.id]: [''] }));
    setActiveKohdeId(kohde.id);
    return kohde;
  };

  const handleKohdeRename = async (id, name) => {
    await updateMittausKohde(id, { nimi: name });
    setProjekt(p => ({ ...p, kohteet: p.kohteet.map(k => k.id === id ? { ...k, nimi: name } : k) }));
  };

  const handleKohdeDelete = async (id) => {
    await deleteMittausKohde(id);
    const kohteet = (projekt?.kohteet ?? []).filter(k => k.id !== id);
    setProjekt(p => ({ ...p, kohteet }));
    setCoverRowsMap(prev => { const c = { ...prev }; delete c[id]; return c; });
    setCarbonRowsMap(prev => { const c = { ...prev }; delete c[id]; return c; });
    if (activeKohdeId === id) setActiveKohdeId(kohteet[0]?.id ?? null);
  };

  const commitMeasurements = useCallback((kohdeId, coverRowsArg, carbRowsArg) => {
    const kohde = (projektRef.current?.kohteet ?? []).find(k => k.id === kohdeId);
    const carbNums = parseRows(carbRowsArg ?? []);
    updateMittausKohde(kohdeId, {
      data: {
        ...kohde?.data,
        cover_measurements: parseRows(coverRowsArg),
        carbonation_measurements: { alapinta: carbNums, ylapinta: [] },
      },
    }).catch(console.error);
  }, []);

  const setCoverRows = useCallback((updater) => {
    setCoverRowsMap(prev => ({ ...prev, [activeKohdeId]: typeof updater === 'function' ? updater(prev[activeKohdeId] ?? ['']) : updater }));
  }, [activeKohdeId]);

  const setCarbonRows = useCallback((updater) => {
    setCarbonRowsMap(prev => ({ ...prev, [activeKohdeId]: typeof updater === 'function' ? updater(prev[activeKohdeId] ?? ['']) : updater }));
  }, [activeKohdeId]);

  const updateAge = useCallback(async (num) => {
    if (!projekt) return;
    const val = num > 0 ? num : null;
    setProjekt(p => ({ ...p, manual_age: val }));
    if (!val) setAgeInput('');
    await updateMittausProjektiMeta(projekt.id, { manual_age: val });
  }, [projekt]);

  const updateTableCaption = (capData) => {
    if (!activeKohdeId) return;
    onChangeRef.current?.({ ...dataRef.current, captions: { ...dataRef.current.captions, [activeKohdeId]: { ...dataRef.current.captions?.[activeKohdeId], table: capData } } });
  };

  const updateChartCaption = (capData) => {
    if (!activeKohdeId) return;
    onChangeRef.current?.({ ...dataRef.current, captions: { ...dataRef.current.captions, [activeKohdeId]: { ...dataRef.current.captions?.[activeKohdeId], chart: capData } } });
  };

  const devFillData = () => {
    if (!activeKohdeId) return;
    const rand = (min, max) => String(Math.round(min + Math.random() * (max - min)));
    const newCoverRows = Array.from({ length: 10 + Math.floor(Math.random() * 5) }, () => rand(6, 45));
    const newCarbonRows = Array.from({ length: 6 + Math.floor(Math.random() * 4) }, () => rand(3, 25));
    setCoverRows(newCoverRows);
    setCarbonRows(newCarbonRows);
    commitMeasurements(activeKohdeId, newCoverRows, newCarbonRows);
  };

  const kohteet     = projekt?.kohteet ?? [];
  const activeKohde = kohteet.find(k => k.id === activeKohdeId) ?? null;
  const coverRows   = coverRowsMap[activeKohdeId] ?? [''];
  const carbonRows  = carbonRowsMap[activeKohdeId] ?? [''];

  const selectedRakennus = projekt?.scope === 'building'
    ? (rakennukset.find(r => r.id_rakennus === projekt.id_rakennus) ?? rakennukset[0])
    : null;

  const buildingYear = (() => {
    const src = projekt?.scope === 'building' ? selectedRakennus : (rakennukset.find(r => r.isMainBuilding) ?? rakennukset[0]);
    const raw = src?.rakennusvuosi;
    if (!raw) return null;
    const m = String(raw).match(/\d{4}/);
    return m ? parseInt(m[0]) : null;
  })();
  const autoAge = buildingYear ? new Date().getFullYear() - buildingYear : null;
  const effectiveAge = parseFloat(projekt?.manual_age) || parseFloat(ageInput) || autoAge || null;

  const coverArr    = useMemo(() => parseRows(coverRows),  [coverRows]);
  const carbonArr   = useMemo(() => parseRows(carbonRows), [carbonRows]);
  const coverStats  = useMemo(() => calcStats(coverArr),  [coverArr]);
  const carbonStats = useMemo(() => calcStats(carbonArr), [carbonArr]);
  const coverDist   = useMemo(() => calcDist(coverArr),   [coverArr]);
  const carbonDist  = useMemo(() => calcDist(carbonArr),  [carbonArr]);

  const rakenneosaGroups = useMemo(() => {
    const groups = [];
    const seen = {};
    kohteet.forEach(k => {
      const name = k.nimi || 'Muu';
      if (!seen[name]) { seen[name] = { name, kohteet: [] }; groups.push(seen[name]); }
      seen[name].kohteet.push(k);
    });
    return groups;
  }, [kohteet]);

  const taulukkoNum = (idx) => tableStartNumber != null ? tableStartNumber + idx : idx + 1;

  if (!id_kiinteisto) return (
    <div>
      <div className="text-muted small p-2">Kiinteistön ID ei saatavilla.</div>
      <TaulukkoFooter taulukkoNum={tableStartNumber ?? 1} kohdeId="preview" caption={null} onChange={() => {}} />
    </div>
  );
  if (loading)        return <div className="text-muted small p-2">Ladataan…</div>;

  const errorBanner = apiError && !showSelector ? (
    <div style={{ fontSize: '0.78rem', color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{apiError}</span>
      <button type="button" onClick={() => setApiError('')} style={{ border: 'none', background: 'transparent', color: '#842029', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }}>×</button>
    </div>
  ) : null;

  const renderResultsTable = (cArr, carbArr, cStats, carbStats, cDist, carbDist, age) => {
    const corrodedD = carbStats.ka ? (() => {
      const corr = cArr.filter(v => v <= carbStats.ka);
      return calcDist(corr).map(b => ({ ...b, pct: cArr.length ? b.count / cArr.length : 0 }));
    })() : null;
    const corrodedT = corrodedD ? corrodedD.reduce((s, b) => s + b.count, 0) : null;
    const coeff = carbStats.ka && age > 0 ? carbStats.ka / Math.sqrt(age) : null;
    const prog  = coeff && age ? YEARS_AHEAD.map(y => coeff * Math.sqrt(age + y)) : null;
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <colgroup>{PEITEPAKSUUS_RANGES.map((_, i) => <col key={i} style={{ width: 58 }} />)}<col style={{ width: 80 }} /></colgroup>
          <thead>
            <tr>
              <th style={CH}>Syvyysalue (mm)</th>
              {PEITEPAKSUUS_RANGES.map(r => <th key={r.label} style={CH}>{r.label}</th>)}
              <th style={CH}>YHT</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={CL}>Osuus peitepaksuushavainnoista</td>
              {cDist.map((b, i) => <td key={i} style={CR}>{b.pct > 0 ? fmtPct(b.pct) : ''}</td>)}
              <td style={{ ...CR, fontWeight: 700 }}>{cArr.length ? '100.0 %' : '—'}</td>
            </tr>
            <tr>
              <td style={CL}>Osuus karbonatisoitumissyvyyshavainnoista</td>
              {carbDist.map((b, i) => <td key={i} style={CR}>{b.pct > 0 ? fmtPct(b.pct) : ''}</td>)}
              <td style={{ ...CR, fontWeight: 700 }}>{carbArr.length ? '100.0 %' : '—'}</td>
            </tr>
            <tr>
              <td style={CL}>Korroosiotilassa olevan raudoituksen kokonaismäärä</td>
              {corrodedD
                ? corrodedD.map((b, i) => <td key={i} style={CR}>{b.pct > 0 ? fmtPct(b.pct) : ''}</td>)
                : PEITEPAKSUUS_RANGES.map((_, i) => <td key={i} style={{ ...CR, color: '#adb5bd' }}>—</td>)}
              <td style={{ ...CR, fontWeight: 700, color: corrodedT ? '#c0392b' : undefined }}>
                {corrodedD ? fmtPct(cArr.length ? corrodedT / cArr.length : 0) : '—'}
              </td>
            </tr>
            <tr><td colSpan={12} style={{ padding: 3, border: 'none' }} /></tr>
            <tr>
              <td style={CLbl}>Betonipeitemittaukset</td>
              <td style={CLbl} colSpan={2}>KESKIARVO</td>
              <td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(cStats.ka)}</td>
              <td style={CLbl}>MIN</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(cStats.min, 0)}</td>
              <td style={CLbl}>MAX</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(cStats.max, 0)}</td>
              <td style={CLbl}>KHAJ</td><td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(cStats.khaj)}</td>
            </tr>
            <tr>
              <td style={CLbl}>Karbonatisoitumismittaukset</td>
              <td style={CLbl} colSpan={2}>KESKIARVO</td>
              <td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(carbStats.ka)}</td>
              <td style={CLbl}>MIN</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(carbStats.min, 0)}</td>
              <td style={CLbl}>MAX</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(carbStats.max, 0)}</td>
              <td style={CLbl}>KHAJ</td><td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(carbStats.khaj)}</td>
            </tr>
            <tr><td colSpan={12} style={{ padding: 3, border: 'none' }} /></tr>
            <tr>
              <td style={CLbl}>Tutkimusajankohdasta</td>
              {YEARS_AHEAD.map(y => <td key={y} style={CH}>{y} v</td>)}
              <td colSpan={2} style={{ ...C, background: '#f8f9fa' }} />
              <td style={CLbl}>Rakenteen ikä (a)</td>
              <td style={CR}>{age ?? '—'}</td>
              <td style={CLbl}>Karb. kerroin</td>
              <td style={{ ...CR, fontWeight: 700 }}>{coeff ? coeff.toFixed(2) : '—'}</td>
            </tr>
            <tr>
              <td style={CL}>Karbonatisoitumisen eteneminen (mm)</td>
              {prog
                ? prog.map((v, i) => <td key={i} style={{ ...CR, fontWeight: 600 }}>{v.toFixed(1)}</td>)
                : YEARS_AHEAD.map((_, i) => <td key={i} style={{ ...CR, color: '#adb5bd' }}>—</td>)}
              <td colSpan={6} style={{ ...C, background: '#fff', fontSize: '0.7rem', color: '#6c757d' }}>
                {coeff ? `k = KESKIARVO / √ikä = ${fmtNum(carbStats.ka)} / √${age} = ${coeff.toFixed(4)}, eteneminen = k × √(ikä + v)` : ''}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Lato', sans-serif", overflowAnchor: 'none' }}>
      {errorBanner}

      {(ptsProjectName || data?.ptsProjectId) && (
        <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: 6 }}>
          {ptsProjectName ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.name ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.nimi ?? `PTS ${data.ptsProjectId}`}
        </div>
      )}
      {/* ── Project header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Tarkastuskohde:</span>
        {allProjektit.length > 0 ? (
          <>
            {allProjektit.map(p => (
              <button key={p.id} type="button" onClick={() => { setActiveProjektiId(p.id); onChange?.({ ...data, mittausProjektiId: p.id }); }}
                style={{ padding: '2px 8px', fontSize: '0.78rem', border: '1px solid', borderRadius: 3, cursor: 'pointer',
                  borderColor: p.id === activeProjektiId ? '#198754' : '#dee2e6',
                  background: p.id === activeProjektiId ? '#f0fdf4' : '#fff',
                  color: p.id === activeProjektiId ? '#15803d' : '#6c757d',
                  fontWeight: p.id === activeProjektiId ? 600 : 400 }}>
                {p.nimi}
              </button>
            ))}
            {projekt && (
              <button type="button" onClick={() => setPendingDeleteProject(true)}
                style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} title="Poista aktiivinen tarkastuskohde">×</button>
            )}
          </>
        ) : projekt ? (
          <>
            {renamingProject ? (
              <input autoFocus value={renameProjectValue} onChange={e => setRenameProjectValue(e.target.value)}
                onBlur={commitRenameProject}
                onKeyDown={e => { if (e.key === 'Enter') commitRenameProject(); if (e.key === 'Escape') setRenamingProject(false); }}
                style={{ fontSize: '0.82rem', fontWeight: 700, border: 'none', borderBottom: '1px solid #198754', outline: 'none', background: 'transparent', padding: '0 2px' }} />
            ) : (
              <span style={{ fontWeight: 600, fontSize: '0.82rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onDoubleClick={() => { setRenameProjectValue(projekt.nimi); setRenamingProject(true); }}>
                {projekt.nimi}
                {projekt.scope === 'property' && <span style={{ marginLeft: 4, opacity: 0.75, fontSize: '0.68rem' }}>🏘</span>}
              </span>
            )}
            <button type="button" onClick={() => setPendingDeleteProject(true)}
              style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} title="Poista tarkastuskohde">×</button>
          </>
        ) : (
          <button type="button" onClick={openSelector}
            style={{ padding: '3px 10px', borderRadius: 20, border: '1px dashed #0d6efd', background: '#fff', color: '#0d6efd', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}>
            + Valitse / luo tarkastuskohde
          </button>
        )}
      </div>

      {!projekt && (
        <div style={{ textAlign: 'center', padding: '28px 16px', color: '#6c757d', borderRadius: 6, background: '#f8f9fa', border: '1px dashed #dee2e6' }}>
          <div style={{ marginBottom: 10, fontSize: '0.9rem' }}>Luo tarkastuskohde aloittaaksesi mittaustulosten syöttämisen.</div>
          <Button variant="success" size="sm" onClick={openSelector}>+ Luo tarkastuskohde</Button>
        </div>
      )}

      {projekt && (
        <>
          {/* ── Kohde tabs ── */}
          <KohdeTabBar
            kohteet={kohteet}
            activeKohdeId={activeKohdeId}
            tableStartNumber={tableStartNumber}
            onActivate={setActiveKohdeId}
            onCreate={handleKohdeCreate}
            onRename={handleKohdeRename}
            onDelete={handleKohdeDelete}
            displayNimi={k => k.nimi}
            withNaytePrompt={!!onCreateNayte}
          />

          {nayteCreateError && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setNayteCreateError(null)}>
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 92vw)', padding: '28px 28px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>⚠️</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>Näytetunnus on jo olemassa</div>
                <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.5 }}>Voit lisätä testejä näytteelle poranäyte taulukosta.</div>
                <button type="button" className="btn btn-warning btn-sm mt-1" onClick={() => setNayteCreateError(null)} style={{ minWidth: 80 }}>OK</button>
              </div>
            </div>
          )}

          {/* ── Meta row ── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {projekt.scope === 'building' && selectedRakennus ? (
              <div style={{ flex: '2 1 160px' }}>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, marginBottom: 2 }}>RAKENNUS</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ ...inputSt, flex: 1, background: '#e9ecef', color: '#495057' }}>
                    {[selectedRakennus.rakennustunnus, selectedRakennus.osoite].filter(Boolean).join(' — ') || `Rakennus ${selectedRakennus.id_rakennus}`}
                  </div>
                  <button type="button" onClick={() => setMapModalRakennus(selectedRakennus)} title="Näytä kartalla"
                    style={{ border: '1px solid #ced4da', borderRadius: 3, background: '#fff', cursor: 'pointer', padding: '2px 7px', color: '#495057' }}>
                    <i className="bi bi-geo-alt" />
                  </button>
                </div>
              </div>
            ) : projekt.scope === 'property' ? (
              <div style={{ flex: '2 1 160px' }}>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, marginBottom: 2 }}>LAAJUUS</div>
                <div style={{ ...inputSt, background: '#e8f5e9', color: '#1b5e20', fontWeight: 600, display: 'flex', alignItems: 'center', minHeight: 28, gap: 6 }}>
                  <span>🏘</span><span>Koko kiinteistö</span>
                </div>
              </div>
            ) : null}
            <div style={{ flex: '0 0 80px' }}>
              <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, marginBottom: 2 }}>
                IKÄ (a)
                {projekt?.manual_age && autoAge && (
                  <button type="button" onClick={() => updateAge(null)}
                    style={{ marginLeft: 5, border: 'none', background: 'transparent', color: '#adb5bd', fontSize: '0.65rem', cursor: 'pointer', padding: 0 }} title="Palauta automaattinen">↩</button>
                )}
              </div>
              <input style={{ ...inputSt, color: ageInput ? '#000' : '#6c757d' }} type="number" min={0} value={ageInput}
                onChange={e => setAgeInput(e.target.value)}
                onBlur={() => { const num = parseFloat(ageInput); updateAge(num > 0 ? num : null); }}
                placeholder={autoAge ? String(autoAge) : 'vuotta'} />
            </div>
            <button type="button" onClick={() => setShowInputs(v => !v)}
              style={{ fontSize: '0.75rem', padding: '3px 10px', border: '1px solid #ced4da', borderRadius: 3, background: showInputs ? '#e9ecef' : '#fff', cursor: 'pointer', alignSelf: 'flex-end' }}>
              {showInputs ? 'Laskut ja kuvaajat' : 'Mittausdata'}
            </button>
          </div>

          {showInputs && (activeKohde ? (
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <MeasurementTable label="BETONIPEITEMITTAUKSET (mm)" rows={coverRows} setRows={setCoverRows}
                  onCommit={(rows) => commitMeasurements(activeKohdeId, rows, carbonRows)} />
              </div>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <MeasurementTable label="KARBONATISOITUMISSYVYYSMITTAUKSET (mm)" rows={carbonRows} setRows={setCarbonRows}
                  rowLabels={activeKohde?.data?.karb_tunnus_labels}
                  onCommit={(rows) => commitMeasurements(activeKohdeId, coverRows, rows)} />
              </div>
            </div>
          ) : (
            <div style={{ padding: '16px 0', color: '#6c757d', fontSize: '0.85rem' }}>
              Lisää rakenneosa (esim. Laatta, Pilari) yllä olevasta <strong>+ Lisää</strong>-painikkeesta aloittaaksesi mittausten syöttämisen.
            </div>
          ))}

          {!showInputs && <>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#495057', marginBottom: 4, padding: '3px 6px', background: '#f8f9fa', borderRadius: 3, display: 'inline-block' }}>
              {projekt.scope === 'property' ? 'TÄMÄ PROJEKTI' : 'TULOKSET'}
              {coverArr.length > 0 && <span style={{ fontWeight: 400, marginLeft: 6, color: '#6c757d' }}>{coverArr.length} kpl</span>}
            </div>
            {renderResultsTable(coverArr, carbonArr, coverStats, carbonStats, coverDist, carbonDist, effectiveAge)}
          </>}

          {!isMobile && !showInputs && coverArr.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ border: '1px solid #dee2e6', borderRadius: 3, padding: '6px 4px 2px' }}>
                <ScatterChart coverArr={coverArr} carbonStats={carbonStats} />
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginTop: 6, color: '#212529' }}>
                Mitattujen terästen peitepaksuudet ja betonin karbonatisoitumissyvyyden vaihteluväli.
              </div>
            </div>
          )}
        </>
      )}

      <TaulukkoFooter
        taulukkoNum={taulukkoNum(Math.max(0, kohteet.findIndex(k => k.id === activeKohdeId)) * 2)}
        kohdeId={activeKohdeId ?? 'preview'}
        caption={data?.captions?.[activeKohdeId]?.table}
        onChange={updateTableCaption}
        label="taulukko"
      />

      {!isMobile && (
        <TaulukkoFooter
          taulukkoNum={taulukkoNum(Math.max(0, kohteet.findIndex(k => k.id === activeKohdeId)) * 2) + 1}
          kohdeId={(activeKohdeId ?? 'preview') + '-chart'}
          caption={data?.captions?.[activeKohdeId]?.chart}
          onChange={updateChartCaption}
          label="kuvaaja"
        />
      )}

      <BuildingMapModal rakennus={mapModalRakennus} show={!!mapModalRakennus} onHide={() => setMapModalRakennus(null)} />

      {/* ── Project selector overlay ── */}
      {showSelector && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowSelector(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(480px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Peitepaksuus-tarkastuskohde</div>
            <div>
              <label style={{ fontSize: '0.82rem', color: '#6c757d', fontWeight: 600, marginBottom: 4, display: 'block' }}>Dynamic PTS -projekti</label>
              <select className="form-select form-select-sm" value={selectedPtsId ?? ''} onChange={e => onPtsChange(Number(e.target.value))}>
                {ptsProjects.map(p => <option key={p.id} value={p.id}>{p.name ?? p.nimi}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6' }}>
              {['new', 'existing'].map(tab => (
                <button key={tab} type="button" onClick={() => setSelectorTab(tab)}
                  style={{ padding: '6px 16px', border: 'none', borderBottom: tab === selectorTab ? '2px solid #0d6efd' : '2px solid transparent', background: 'none', color: tab === selectorTab ? '#0d6efd' : '#6c757d', fontWeight: tab === selectorTab ? 600 : 400, fontSize: '0.88rem', cursor: 'pointer' }}>
                  {tab === 'new' ? 'Uusi tarkastuskohde' : `Olemassa olevat (${existingProjektit.length})`}
                </button>
              ))}
            </div>
            {selectorTab === 'new' ? (
              <>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Tarkastuskohteen nimi</label>
                  <input className="form-control form-control-sm" autoFocus value={newProjName}
                    onChange={e => { setNewProjName(e.target.value); setProjNameError(''); }}
                    onKeyDown={e => e.key === 'Enter' && newProjName.trim() && createProject()}
                    style={{ borderColor: projNameError ? '#dc3545' : undefined }} />
                  {projNameError && <div style={{ fontSize: '0.7rem', color: '#dc3545', marginTop: 3 }}>{projNameError}</div>}
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Päivämäärä</label>
                  <input className="form-control form-control-sm" type="date" value={newProjDate} onChange={e => setNewProjDate(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 6 }}>Laajuus</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['property', 'Koko kiinteistö'], ['building', 'Yksittäinen rakennus']].map(([scope, label]) => (
                      <button key={scope} type="button"
                        onClick={() => { setNewProjScope(scope); setNewProjName(suggestedName(scope, newProjRakennusId, newProjDate)); setProjNameError(''); }}
                        style={{ flex: 1, padding: '7px 12px', border: '1px solid', borderRadius: 4, borderColor: newProjScope === scope ? '#198754' : '#ced4da', background: newProjScope === scope ? '#198754' : '#fff', color: newProjScope === scope ? '#fff' : '#495057', fontSize: '0.82rem', cursor: 'pointer', fontWeight: newProjScope === scope ? 600 : 400 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {newProjScope === 'building' && rakennukset.length > 0 && (
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', display: 'block', marginBottom: 3 }}>Rakennus</label>
                    <select className="form-select form-select-sm" value={newProjRakennusId ?? ''}
                      onChange={e => { const rid = parseInt(e.target.value); setNewProjRakennusId(rid); setNewProjName(suggestedName('building', rid, newProjDate)); setProjNameError(''); }}>
                      {rakennukset.map(r => <option key={r.id_rakennus} value={r.id_rakennus}>{[r.rakennustunnus, r.osoite, r.toimipaikka].filter(Boolean).join(' — ') || `Rakennus ${r.id_rakennus}`}</option>)}
                    </select>
                  </div>
                )}
                {apiError && (
                  <div style={{ fontSize: '0.78rem', color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px' }}>
                    {apiError}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Peruuta</button>
                  <button className="btn btn-sm btn-success" onClick={createProject} disabled={!selectedPtsId || !newProjName.trim()}>Luo tarkastuskohde</button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {existingProjektit.length === 0
                  ? <div style={{ fontSize: '0.88rem', color: '#6c757d' }}>Ei peitepaksuus-tarkastuskohteita tässä PTS-projektissa.</div>
                  : existingProjektit.map(p => (
                    <button key={p.id} type="button" onClick={() => linkProject(p.id)}
                      style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p.nimi}
                    </button>
                  ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Sulje</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete project confirmation ── */}
      {pendingDeleteProject && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteProject(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista tarkastuskohde</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>Haluatko poistaa tarkastuskohteen <strong>{projekt?.nimi}</strong> ja sen kaiken mittausdatan?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteProject(false)}>Peruuta</button>
              <button className="btn btn-sm btn-danger" onClick={confirmDeleteProject}>Poista</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
